import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout, StatusBadge, TierBadge } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Link, useNavigate } from 'react-router-dom';
import ApplicationAnswers from '../../components/ApplicationAnswers';
import ProposalTermsEditor from '../../components/ProposalTermsEditor';
import PartnerPaperwork from '../../components/PartnerPaperwork';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { renderMarkdown } from '../../components/TermsDocument';
import { Search, Filter, MoreVertical, Eye, CheckCircle, XCircle, Download, KeyRound, FileText, ScrollText, FileSignature, Loader2, LogIn } from 'lucide-react';

// The answers, pulled off a partner record into the shape `ApplicationAnswers` edits. The
// list endpoint returns whole records, so this is the one place that knows which of their
// fields are answers — the labels themselves live in lib/application.js, shared with the
// partner's own copy of this form.
//
// `APPLICATION_ANSWERS`, `BAND_STYLE` and `IcpBadge` were here. They rendered the ICP band,
// which nothing computes any more (see the header of backend/scorecard.py).
const applicationAnswersOf = (partner) => ({
  ...Object.fromEntries(
    ['business_type', 'client_count', 'team_size', 'retainer_share', 'delivery_capability',
      'vertical_fit', 'commercial_posture', 'company_name', 'website', 'linkedin_url',
      'designation', 'city', 'state'].map((f) => [f, partner?.[f] || ''])
  ),
  runs_on: partner?.runs_on || [],
});

/**
 * Where a partner is in the journey — an axis of its own, next to Status.
 *
 * Status says whether they are in good standing; this says what has actually moved. Every stage
 * is an artefact: a proposal went out, they said yes, a lead came in, money started. `proposal_sent`
 * is AMBER because it is the one stage that is purely "waiting on them" — the other thing worth
 * chasing is an unaccepted agreement, and that is now a line, not a colour.
 *
 * The agreement is NOT here, and neither is onboarding. Both are STATUSES — true or not, in any
 * order, blocking nothing but payouts — and a stage can only ever show the furthest thing, so
 * putting the agreement on this axis hid it behind the first lead. It is rendered as its own line
 * below the pill instead, which is visible wherever the partner has got to.
 */
const STAGE = {
  applied:            { label: 'Applied',            cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  approved:           { label: 'Approved',           cls: 'bg-violet-100 text-violet-700 border-violet-200' },
  proposal_sent:      { label: 'Proposal sent',      cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  proposal_accepted:  { label: 'Proposal accepted',  cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  first_lead:         { label: 'First lead',         cls: 'bg-lime-100 text-lime-700 border-lime-200' },
  first_conversion:   { label: 'First conversion',   cls: 'bg-green-100 text-green-700 border-green-200' },
  commission_earned:  { label: 'Commission earned',  cls: 'bg-green-600 text-white border-green-600' },
  dormant:            { label: 'Dormant',            cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  churned:            { label: 'Churned',            cls: 'bg-red-100 text-red-700 border-red-200' },
};

// The stages that mean a partner is actually earning. `productive` used to be the single
// green pill for all of it; it is retired because it answered neither "have they converted
// anyone" nor "are we paying them", and counted clients who arrived with no lead at all.
const EARNING_STAGES = ['first_conversion', 'commission_earned'];

const shortDate = (str) =>
  str ? new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';

/** The stage, plus the one line that says what is owed and by whom. */
const JourneyCell = ({ partner }) => {
  const stage = STAGE[partner.stage] || STAGE.applied;
  const proposal = partner.proposal;
  const agreement = partner.agreement;

  // A partner earning without an accepted agreement is not an edge case — payouts and credit
  // purchases are gated on it, so this is someone who cannot be paid. It has to be louder than
  // the green pill above it, or the pill quietly says everything is fine.
  const earningUnsigned = EARNING_STAGES.includes(partner.stage) && agreement?.status !== 'signed';

  let note = '';
  if (partner.stage === 'proposal_sent') note = `sent ${shortDate(proposal?.created_at)} · waiting on them`;
  else if (partner.stage === 'proposal_accepted') note = `accepted ${shortDate(proposal?.decided_at)}`;
  // Dormant is the one stage that is about ABSENCE, so the note has to say since when —
  // a partner reading "Dormant" with no date tells you nothing you can act on.
  else if (partner.stage === 'dormant') note = 'no lead in over a month';

  // The agreement, wherever they have got to. Read off the agreement itself rather than the
  // stage, which is the whole point of taking it off the stage axis.
  const agreementNote = !agreement ? ''
    : agreement.status === 'signed'
      ? `agreement accepted ${shortDate(agreement.signed_at)}`
      : `agreement issued ${shortDate(agreement.issued_at)} · waiting on them`;

  return (
    <div data-testid="journey-cell">
      <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold ${stage.cls}`}>
        {stage.label}
      </span>
      {earningUnsigned && (
        <p className="mt-1 text-[10px] font-semibold text-red-600">
          {agreement ? 'agreement not accepted' : 'no agreement'} · cannot be paid
        </p>
      )}
      {!earningUnsigned && note && <p className="mt-1 text-[10px] text-[#64748B]">{note}</p>}
      {!earningUnsigned && agreementNote && (
        <p className="mt-0.5 text-[10px] text-[#64748B]">{agreementNote}</p>
      )}
    </div>
  );
};

/**
 * EVERY rate and EVERY gate this partner is on, in one place.
 *
 * They were spread across three screens and a proposal: the commission ladder on the rate
 * card page, the wallet floor inside the reseller copy, the white-label gate nowhere at all,
 * and the only way to see what a given partner was actually on was to open the proposal you
 * sent them. So "what is this partner paying and what are they gated at" could not be
 * answered from the partner record — which is the one place anybody looks.
 *
 * Read-only, with one button. Terms move ONLY on a proposal the partner accepts: that is the
 * audit trail, and `pinned_rate_card_version` on their record is what freezes it. An edit box
 * here would change someone's commercial terms with no document behind it.
 *
 * Anything that is not the published number is marked, with the published number beside it,
 * because a negotiated rate does not expire and nobody re-papers what they cannot see.
 */
/**
 * Branding: one switch, deliberately small and deliberately separate from the numbers.
 *
 * ALL an admin does here is grant the TIER — the permission to run on their own brand. They do
 * NOT pick the subdomain. The partner picks that on their own Branding page, where they get
 * live availability, suggestions and the refusals (a reserved word, a name containing "dev"
 * that would misroute their voice webhooks). Choosing someone else's product name for them,
 * from a screen with none of those checks, is how a partner ends up with an address they did
 * not want and cannot change once clients have bookmarked it.
 *
 * Tier 3 is the exception: the hostname is a domain THEY own, so there is nothing to pick from
 * a list and it is entered here.
 */
const BrandingGrant = ({ partner }) => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tier, setTier] = useState(1);
  const [appName, setAppName] = useState('');
  const [host, setHost] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/partners/${partner.id}/branding`,
                                { headers: getAuthHeaders() });
      setData(r.data);
      const b = r.data.brand || {};
      setTier(b.tier || 1);
      setAppName(b.app_name || partner.company_name || '');
    } catch {
      setData({ error: true });
    }
  }, [partner.id, partner.company_name, getAuthHeaders, API_URL]);

  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/partners/${partner.id}/branding`,
        { tier: Number(tier), app_name: appName, slug: tier === 3 ? host || null : null },
        { headers: getAuthHeaders() });
      toast.success('Branding enabled. They pick their address on their own Branding page.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  if (!data || data.error) return null;
  const brand = data.brand || {};
  const hosts = (brand.hostnames || []).map((h) => h.hostname);

  return (
    <div className="rounded-xl border border-[#E2E2DF] bg-[#FAFAF8] p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-wider text-[#64748B]">Branding</p>
        {brand.granted
          ? <span className="text-xs font-medium text-green-800 bg-green-100 rounded px-2 py-0.5">
              Tier {brand.tier} · enabled
            </span>
          : <span className="text-xs font-medium text-[#64748B] bg-[#EDEDEA] rounded px-2 py-0.5">
              Off
            </span>}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <p className="text-xs text-[#64748B] mb-1">Level</p>
          <select value={tier} onChange={(e) => setTier(Number(e.target.value))}
                  className="w-full h-9 rounded-md border border-[#E2E2DF] px-2 text-sm bg-white">
            <option value={1}>1 — co-brand, on our domain</option>
            <option value={2} disabled={!data.zones?.tier2}>
              2 — neutral domain{data.zones?.tier2 ? '' : ' (not configured)'}
            </option>
            <option value={3}>3 — full white label, their own domain</option>
          </select>
        </div>
        <div>
          <p className="text-xs text-[#64748B] mb-1">
            {tier === 3 ? 'Their domain' : 'Product name (they can change it)'}
          </p>
          {tier === 3
            ? <Input value={host} onChange={(e) => setHost(e.target.value.trim())}
                     placeholder="crm.sharma.com" className="h-9" />
            : <Input value={appName} onChange={(e) => setAppName(e.target.value)}
                     placeholder={partner.company_name || 'Their product name'} className="h-9" />}
        </div>
      </div>

      <p className="text-xs text-[#64748B]">
        {hosts.length
          ? <>Address: <span className="font-mono">{hosts.join(', ')}</span> — they chose it.</>
          : tier === 3
            ? 'They point this domain at us; we issue the certificate.'
            : 'They pick their own address on their Branding page, with availability checked live.'}
      </p>

      <Button onClick={grant} disabled={busy || (tier !== 3 && !appName)} size="sm"
              data-testid="grant-branding">
        {busy ? 'Saving…' : brand.granted ? 'Update' : 'Enable branding'}
      </Button>
    </div>
  );
};

const RatesAndGatesPanel = ({ partner, onSendProposal }) => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/partners/${partner.id}/rate-card`, { headers: getAuthHeaders() })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [partner.id, getAuthHeaders, API_URL]);

  if (!data) return <p className="text-sm text-[#64748B]">Loading rates…</p>;
  if (data.error) return <p className="text-sm text-[#64748B]">Could not load this partner's rates.</p>;

  const card = data.card || {};
  const base = data.base || {};
  const changed = data.changed || {};
  const order = card.tier_order || ['silver', 'gold', 'platinum'];

  const money = (n) => (n == null ? '—' : '₹' + Math.round(Number(n)).toLocaleString('en-IN'));
  const pct = (n) => (n == null ? '—' : `${Math.round(Number(n) * 1000) / 10}%`);
  const rate = (v) => (v == null ? '—' : typeof v === 'object' ? `${money(v.flat)}/client` : pct(v));
  const at = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);

  /** One number, with the published one beside it when they differ. */
  const Cell = ({ path, format }) => {
    const mine = at(card, path);
    const std = at(base, path);
    const isChanged = path in changed;
    return (
      <span>
        <span className={isChanged ? 'font-semibold text-[#1A1A1A]' : 'text-[#1A1A1A]'}>
          {format(mine)}
        </span>
        {isChanged && (
          <span className="ml-1.5 text-[10px] uppercase tracking-wide font-medium
                           text-[#B45309] bg-[#FEF3C7] rounded px-1 py-0.5 whitespace-nowrap">
            was {format(std)}
          </span>
        )}
      </span>
    );
  };

  const Row = ({ label, children, hint }) => (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-[#EDEDEA] last:border-0">
      <div>
        <span className="text-sm text-[#1A1A1A]">{label}</span>
        {hint && <p className="text-xs text-[#64748B]">{hint}</p>}
      </div>
      <div className="text-sm text-right">{children}</div>
    </div>
  );

  const Group = ({ title, children }) => (
    <div>
      <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">{title}</p>
      {children}
    </div>
  );

  const negotiatedCount = Object.keys(changed).length;

  return (
    <div className="rounded-xl border border-[#E2E2DF] bg-[#FAFAF8] p-4 space-y-5"
         data-testid="rates-and-gates">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-wider text-[#64748B]">Rates &amp; gates</p>
        <div className="flex items-center gap-2">
          {negotiatedCount > 0 && (
            <span className="text-xs font-medium text-[#B45309] bg-[#FEF3C7] rounded px-2 py-0.5">
              {negotiatedCount} negotiated
            </span>
          )}
          <span className="text-xs text-[#64748B]">
            card v{data.pinned_version ?? data.base_version}
            {data.pinned_version && data.pinned_version !== data.base_version
              ? ` · published is v${data.base_version}` : ''}
          </span>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Group title="Commission — what we pay them">
          {['solution', 'referral', 'affiliate'].map((arr) => (
            <Row key={arr} label={arr.charAt(0).toUpperCase() + arr.slice(1)}>
              {order.map((t) => (
                <span key={t} className="ml-3 whitespace-nowrap">
                  <span className="text-[#64748B] text-xs capitalize">{t.slice(0, 1)}</span>{' '}
                  <Cell path={`commission.${arr}.${t}`} format={rate} />
                </span>
              ))}
            </Row>
          ))}
          <Row label="Tier reached at" hint="earned in a quarter">
            {order.filter((t) => t !== 'silver').map((t) => (
              <span key={t} className="ml-3 whitespace-nowrap">
                <span className="text-[#64748B] text-xs capitalize">{t.slice(0, 1)}</span>{' '}
                <Cell path={`tier_thresholds.${t}`} format={money} />
              </span>
            ))}
          </Row>
        </Group>

        <Group title="Reseller — the wallet">
          <Row label="Minimum purchase" hint="the gate — below it there is no wallet">
            <Cell path="wallet.floor" format={money} />
          </Row>
          <Row label="Discount by tier">
            {order.map((t) => (
              <span key={t} className="ml-3 whitespace-nowrap">
                <span className="text-[#64748B] text-xs capitalize">{t.slice(0, 1)}</span>{' '}
                <Cell path={`wallet.discount.${t}`} format={pct} />
              </span>
            ))}
          </Row>
          <Row label="Client seed and refill">
            <Cell path="wallet.min_client_seed" format={money} />
          </Row>
          <Row label="Credits expire">
            <span className="whitespace-nowrap">
              <Cell path="wallet.expiry_months" format={(v) => `${v ?? '—'} mo`} />
              <span className="text-[#64748B] text-xs"> in wallet · </span>
              <Cell path="wallet.client_expiry_months" format={(v) => `${v ?? '—'} mo`} />
              <span className="text-[#64748B] text-xs"> in client</span>
            </span>
          </Row>
        </Group>

        <Group title="White label — the branding gate">
          <Row label="Co-brand minimum / month">
            <Cell path="whitelabel.cobrand_min_monthly" format={money} />
          </Row>
          <Row label="Co-brand rate">
            <Cell path="whitelabel.cobrand_rate" format={money} />
          </Row>
          <Row label="Full white-label minimum / month">
            <Cell path="whitelabel.full_min_monthly" format={money} />
          </Row>
          <Row label="Full white-label rate">
            <Cell path="whitelabel.full_rate" format={money} />
          </Row>
        </Group>

        <Group title="Change any of it">
          <p className="text-xs text-[#64748B] mt-1">
            Every number above moves the same way: send a proposal with the new terms, and it
            takes effect when they accept it. That acceptance is what pins their card, so there
            is always a document behind a rate.
          </p>
          <Button size="sm" className="mt-3" onClick={onSendProposal} data-testid="edit-terms">
            Send a new proposal
          </Button>
        </Group>
      </div>
    </div>
  );
};

/** The two credit columns: what a partner bought from us, and what they passed on.
 *
 *  Credits are a COUNT, not money, so they are formatted as one — the rupees underneath are
 *  what was actually paid for them, which is the number that reconciles against a bank
 *  statement. Bought is lifetime; a partner who bought last year and let it lapse still
 *  bought it. Available is live only, because expired credits are not refunded and must
 *  never read as spendable.
 *
 *  A dash is not a zero. Until the platform answers we do not know, and printing 0 for
 *  "unknown" is how a partner with a full wallet reads as having bought nothing.
 */
const CreditCells = ({ position, loaded }) => {
  const count = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);
  const rupees = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
      .format(n || 0);

  if (!loaded) {
    return (
      <>
        <TableCell className="text-sm text-[#64748B]">…</TableCell>
        <TableCell className="text-sm text-[#64748B]">…</TableCell>
      </>
    );
  }
  if (!position || !position.credits_bought) {
    return (
      <>
        <TableCell className="text-sm text-[#64748B]">—</TableCell>
        <TableCell className="text-sm text-[#64748B]">—</TableCell>
      </>
    );
  }
  return (
    <>
      <TableCell data-testid="credits-bought">
        <p className="font-mono font-medium tabular-nums text-[#1A1A1A]">
          {count(position.credits_bought)}
        </p>
        <p className="font-mono text-[11px] text-[#64748B]">{rupees(position.cash_paid)} paid</p>
      </TableCell>
      <TableCell data-testid="credits-given">
        <p className="font-mono font-medium tabular-nums text-[#1A1A1A]">
          {count(position.credits_given)}
        </p>
        <p className="font-mono text-[11px] text-[#64748B]">
          {count(position.credits_available)} left
          {position.credits_lapsed > 0 ? ` · ${count(position.credits_lapsed)} lapsed` : ''}
        </p>
      </TableCell>
    </>
  );
};

const AdminPartners = () => {
  const { getAuthHeaders, API_URL, startSupportSession } = useAuth();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  // Two of these are questions rather than filters — "who has not accepted yet" and "who is
  // earning without having accepted" are the reasons this screen gets opened, and they were
  // previously answerable only by opening every partner in turn.
  const [stageFilter, setStageFilter] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState(null);
  // Fallback for a partner whose inbox cannot receive the reset link — the admin sets a
  // password here and passes it on by phone. Everyone else uses "Forgot password".
  const [decisionTarget, setDecisionTarget] = useState(null); // { partner, status }
  const [decisionReason, setDecisionReason] = useState('');
  const [proposalTarget, setProposalTarget] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [lastProposal, setLastProposal] = useState(null);
  // The numbers changed on this proposal before sending — a sparse diff, empty for a
  // standard offer. See ProposalTermsEditor.
  const [termsDiff, setTermsDiff] = useState({});
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  // What this partner has agreed to — pinned card, proposals, terms acceptances. Loaded on
  // demand rather than with the list: it is three collections per partner and nobody needs it
  // until they ask for one.
  const [agreementTarget, setAgreementTarget] = useState(null);
  const [agreement, setAgreement] = useState(null);
  // The agreement TEXT. A fingerprint you cannot compare a document against proves nothing, so
  // the record has to be readable from the same place the fingerprint is shown.
  const [agreementDoc, setAgreementDoc] = useState(null);

  /* Credits live on the platform, not here, so they arrive in their own call and merge into
     the rows by partner id. Deliberately not folded into /admin/partners: doing that would
     take this whole page down whenever the platform restarts. The table renders either way
     and only the credit columns go quiet. */
  const [creditPositions, setCreditPositions] = useState(null);

  useEffect(() => {
    fetchPartners();
  }, [statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/credit-positions`, {
          headers: getAuthHeaders(),
        });
        setCreditPositions(res.data?.positions || {});
      } catch {
        setCreditPositions({});   // platform unreachable: show dashes, not an error page
      }
    })();
  }, [API_URL, getAuthHeaders]);

  const fetchPartners = async () => {
    try {
      let url = `${API_URL}/api/admin/partners`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    } finally {
      setLoading(false);
    }
  };

  /** Issue (or re-issue) the executed agreement by hand.
   *
   *  Approval issues it automatically — this is for the ordinary case where the partner filled
   *  in their particulars AFTER they were approved, which is most of them. */
  const issueAgreement = async (partnerId) => {
    try {
      await axios.post(`${API_URL}/api/admin/partners/${partnerId}/agreement`, null,
                       { headers: getAuthHeaders() });
      toast.success('Agreement issued — the partner has been asked to accept it');
      if (agreementTarget?.id === partnerId) openAgreement(agreementTarget);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not issue the agreement');
    }
  };

  /** Open the agreement record for one partner. */
  const openAgreement = async (partner) => {
    setAgreementTarget(partner);
    setAgreement(null);
    setAgreementDoc(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/partners/${partner.id}/agreement`,
        { headers: getAuthHeaders() }
      );
      setAgreement(res.data);
    } catch (e) {
      toast.error('Could not load the agreement record');
      setAgreementTarget(null);
    }
  };

  /** Fetch the agreement text itself, on demand. */
  const readAgreement = async (partnerId) => {
    if (agreementDoc) { setAgreementDoc(null); return; }
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/partners/${partnerId}/agreement/document`,
        { headers: getAuthHeaders() }
      );
      setAgreementDoc(res.data.document);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the agreement');
    }
  };

  /** Record the qualifying answers, then generate the proposal from them. Two calls because
      they are two different things: what we learned about their business, and the offer built
      from it. Neither number is typed into the proposal itself. */
  /** One call, one argument. A proposal is the rate card offered whole, so there is nothing to
      record off a call first and nothing to configure. */
  const generateProposal = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/proposals`,
        { partner_id: proposalTarget.id,
          terms_diff: Object.keys(termsDiff).length ? termsDiff : null },
        { headers: getAuthHeaders() }
      );
      setLastProposal(res.data);
      toast.success('Proposal generated and sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate the proposal');
    } finally {
      setGenerating(false);
    }
  };

  const updateStatus = async (partnerId, status, reason = '') => {
    try {
      await axios.put(
        `${API_URL}/api/admin/partners/${partnerId}/status?status=${status}` +
          (reason ? `&reason=${encodeURIComponent(reason)}` : ''),
        null,
        { headers: getAuthHeaders() }
      );
      toast.success(`Partner status updated to ${status}`);
      setDecisionTarget(null);
      setDecisionReason('');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updateTier = async (partnerId, tier) => {
    try {
      await axios.put(`${API_URL}/api/admin/partners/${partnerId}/tier?tier=${tier}`, null, {
        headers: getAuthHeaders()
      });
      toast.success(`Partner tier updated to ${tier}`);
      fetchPartners();
    } catch (error) {
      toast.error('Failed to update tier');
    }
  };

  const setPartnerPassword = async (e) => {
    e.preventDefault();
    setSavingPassword(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/partners/${passwordTarget.id}/set-password`,
        { password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success(`Password set for ${passwordTarget.full_name}. Pass it on directly.`);
      setPasswordTarget(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setSavingPassword(false);
    }
  };

  const filteredPartners = partners.filter(partner => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = partner.full_name.toLowerCase().includes(term) ||
      partner.email.toLowerCase().includes(term) ||
      (partner.company_name || '').toLowerCase().includes(term);
    const matchesTier = tierFilter === 'all' || partner.tier === tierFilter;
    const matchesStage =
      stageFilter === 'all' ? true
        : stageFilter === 'awaiting_them'
          ? partner.stage === 'proposal_sent'
            || (partner.agreement && partner.agreement.status !== 'signed')
          : stageFilter === 'earning_unsigned'
            ? EARNING_STAGES.includes(partner.stage) && partner.agreement?.status !== 'signed'
            : stageFilter === 'agreement_unsigned'
              ? !partner.agreement || partner.agreement.status !== 'signed'
              : partner.stage === stageFilter;
    return matchesSearch && matchesTier && matchesStage;
  });

  /* Open a partner's own portal as them, to help with something they are stuck on.
   *
   * Unlike "Set password" below it, this costs the partner nothing: their password is not read
   * (it cannot be — only the hash is stored) and not changed, and their own sessions carry on
   * uninterrupted. The trade is that the session is logged and the partner is told it happened,
   * and that every audited action inside it records the admin rather than them. */
  const openTheirPortal = async (partner) => {
    try {
      await startSupportSession(partner.id);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not open that partner\'s portal.');
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Company', 'Email', 'Phone', 'Tier', 'Status', 'Active Clients',
                     'Client MRR', 'Credits Bought', 'Cash Paid For Credits',
                     'Credits Given Away', 'Credits Available', 'Credits Lapsed', 'Joined'];
    const rows = filteredPartners.map(p => {
      // Blank, not 0, when the platform did not answer — an export that says a partner
      // bought nothing is worse than one that says we do not know.
      const c = creditPositions?.[p.id];
      const num = (v) => (c ? v : '');
      return [
        p.full_name,
        p.company_name || '',
        p.email,
        p.mobile || '',
        p.tier,
        p.status,
        p.active_clients || 0,
        p.total_revenue || 0,
        num(c?.credits_bought ?? 0),
        num(c?.cash_paid ?? 0),
        num(c?.credits_given ?? 0),
        num(c?.credits_available ?? 0),
        num(c?.credits_lapsed ?? 0),
        formatDate(p.created_at),
      ];
    });
    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partners-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-partners">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">
              All Partners
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              Manage and monitor all partner accounts
            </p>
          </div>
          <Button variant="outline" className="border-[#E2E2DF]" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Search by name, company or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 border-[#E2E2DF]"
                  data-testid="search-partners"
                />
              </div>
              <div className="flex gap-3">
                <Select value={stageFilter} onValueChange={setStageFilter}>
                  <SelectTrigger className="w-48 h-10 border-[#E2E2DF]" data-testid="filter-stage">
                    <Filter className="w-4 h-4 mr-2 text-[#64748B]" />
                    <SelectValue placeholder="Journey" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Whole journey</SelectItem>
                    <SelectItem value="awaiting_them">⏳ Waiting on them</SelectItem>
                    <SelectItem value="earning_unsigned">⚠ Earning, not accepted</SelectItem>
                    <SelectItem value="agreement_unsigned">📄 No accepted agreement</SelectItem>
                    <SelectItem value="applied">Applied</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="proposal_sent">Proposal sent</SelectItem>
                    <SelectItem value="proposal_accepted">Proposal accepted</SelectItem>
                    <SelectItem value="first_lead">First lead</SelectItem>
                    <SelectItem value="first_conversion">First conversion</SelectItem>
                    <SelectItem value="commission_earned">Commission earned</SelectItem>
                    <SelectItem value="dormant">Dormant</SelectItem>
                    <SelectItem value="churned">Churned</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36 h-10 border-[#E2E2DF]" data-testid="filter-status">
                    <Filter className="w-4 h-4 mr-2 text-[#64748B]" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={tierFilter} onValueChange={setTierFilter}>
                  <SelectTrigger className="w-32 h-10 border-[#E2E2DF]" data-testid="filter-tier">
                    <SelectValue placeholder="Tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="silver">Silver</SelectItem>
                    <SelectItem value="gold">Gold</SelectItem>
                    <SelectItem value="platinum">Platinum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border-[#E2E2DF] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Partner Name</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Company</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Phone</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Tier</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Active Clients</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Client MRR</TableHead>
                  {/* Two columns, not one: what they PUT IN and what they have PASSED ON are
                      different questions. A partner sitting on a big unspent balance and one
                      who has given every credit away look identical on a single number. */}
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Credits Bought</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Given Away</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Joined</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Journey</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredPartners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center py-8 text-[#64748B]">
                      No partners found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPartners.map((partner, index) => (
                    <TableRow key={partner.id} className="hover:bg-[#FAFAF8]/50" data-testid={`partner-row-${index}`}>
                      <TableCell>
                        <div
                          className="cursor-pointer hover:opacity-80"
                          onClick={() => setSelectedPartner(partner)}
                        >
                          {/* The ICP band was here, as the dialling order. Nothing computes one
                              any more — see the header of backend/scorecard.py — and the badge
                              had stopped rendering for every partner. What orders this queue now
                              is the answers themselves, in the record. */}
                          <p className="font-medium text-[#ef785b] underline">{partner.full_name}</p>
                          <p className="text-xs text-[#64748B]">{partner.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {/* The agency, which is what a partnership is actually with: one company
                            is one partner_id is one agreement. An em dash rather than a blank so
                            a missing company reads as missing rather than as a rendering slip —
                            it is the field a wallet, a deal and an agreement all hang off. */}
                        {partner.company_name ? (
                          <span className="text-sm text-[#1A1A1A]">{partner.company_name}</span>
                        ) : (
                          <span className="text-sm text-[#94A3B8]">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {partner.mobile ? (
                          <a href={`tel:${partner.mobile}`} className="text-sm text-[#ef785b] underline">
                            {partner.mobile}
                          </a>
                        ) : (
                          <span className="text-sm text-[#64748B]">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select value={partner.tier} onValueChange={(v) => updateTier(partner.id, v)}>
                          <SelectTrigger className="h-8 w-28 border-[#E2E2DF] text-xs capitalize" data-testid={`tier-select-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="silver">Silver</SelectItem>
                            <SelectItem value="gold">Gold</SelectItem>
                            <SelectItem value="platinum">Platinum</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="font-medium text-[#1A1A1A]">{partner.active_clients || 0}</TableCell>
                      <TableCell className="font-medium text-green-600">{formatCurrency(partner.total_revenue || 0)}</TableCell>
                      <CreditCells position={creditPositions?.[partner.id]} loaded={creditPositions !== null} />
                      <TableCell className="text-sm text-[#64748B]">{formatDate(partner.created_at)}</TableCell>
                      <TableCell>
                        <JourneyCell partner={partner} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={partner.status} />
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`partner-actions-${index}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setSelectedPartner(partner)}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {partner.status === 'pending' && (
                              <DropdownMenuItem onClick={() => setDecisionTarget({ partner, status: 'active' })}>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Approve
                              </DropdownMenuItem>
                            )}
                            {partner.status === 'active' && (
                              <DropdownMenuItem onClick={() => setDecisionTarget({ partner, status: 'suspended' })}>
                                <XCircle className="w-4 h-4 mr-2 text-red-600" /> Suspend
                              </DropdownMenuItem>
                            )}
                            {partner.status === 'suspended' && (
                              <DropdownMenuItem onClick={() => setDecisionTarget({ partner, status: 'active' })}>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Reactivate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => {
                                setProposalTarget(partner);
                                setLastProposal(null);
                              }}
                              data-testid={`send-proposal-${index}`}
                            >
                              <FileText className="w-4 h-4 mr-2 text-[#64748B]" /> Send a proposal
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openAgreement(partner)}
                              data-testid={`view-agreement-${index}`}
                            >
                              <ScrollText className="w-4 h-4 mr-2 text-[#64748B]" /> View agreement
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => issueAgreement(partner.id)}
                              data-testid={`issue-agreement-${index}`}
                            >
                              <FileSignature className="w-4 h-4 mr-2 text-[#64748B]" /> Issue agreement
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openTheirPortal(partner)}
                              data-testid={`support-session-${index}`}
                            >
                              <LogIn className="w-4 h-4 mr-2 text-[#64748B]" /> Sign in as this partner
                            </DropdownMenuItem>
                            {/* Below "sign in as", deliberately. This one CHANGES their real
                                password and locks them out until somebody reads them the new
                                one; it is the fallback for a partner whose email is broken,
                                not the way to help them with something. */}
                            <DropdownMenuItem onClick={() => setPasswordTarget(partner)} data-testid={`set-password-${index}`}>
                              <KeyRound className="w-4 h-4 mr-2 text-[#64748B]" /> Set password
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Approve / suspend, with the reason. Deliberately a human judgment — we do not yet
            know what to look at well enough to encode it, and an unexplained decision teaches
            us nothing. The reasons typed here are what the ICP scorecard gets written from. */}
        <Dialog open={!!decisionTarget} onOpenChange={() => { setDecisionTarget(null); setDecisionReason(''); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {decisionTarget?.status === 'active' ? 'Approve' : 'Suspend'} {decisionTarget?.partner?.full_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-[#64748B]">
                {decisionTarget?.status === 'active'
                  ? 'What made this one a yes? One line is enough — these answers are what the qualification criteria get written from.'
                  : 'Why are you suspending them?'}
              </p>
              <Input
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
                placeholder={decisionTarget?.status === 'active'
                  ? 'e.g. 14 retainer clients in education, already sells automation'
                  : 'e.g. no activity in 6 months'}
                data-testid="decision-reason"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDecisionTarget(null); setDecisionReason(''); }}>
                Cancel
              </Button>
              <Button
                onClick={() => updateStatus(decisionTarget.partner.id, decisionTarget.status, decisionReason)}
                data-testid="confirm-decision"
              >
                {decisionTarget?.status === 'active' ? 'Approve' : 'Suspend'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send a proposal — Stage 3. There is nothing to fill in: a proposal is the rate card
            they are pinned to, offered whole, with every arrangement explained. It used to ask
            for a client count and a buying cadence, which made it a quote — and a quote over a
            book that changes every month is a promise you cannot keep. */}
        <Dialog open={!!proposalTarget} onOpenChange={() => { setProposalTarget(null); setLastProposal(null); setTermsDiff({}); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Send a proposal to {proposalTarget?.full_name}</DialogTitle>
            </DialogHeader>

            {!lastProposal ? (
              <div className="space-y-4">
                <p className="text-sm text-[#64748B]">
                  A page they read and accept: affiliate, referral, solution and reseller, what
                  each pays, and what each asks of them. Accepting <strong className="text-[#1A1A1A]">
                  freezes these numbers to them</strong> — a later change to the published card
                  will not move them, and re-negotiating means sending another proposal.
                </p>
                <ProposalTermsEditor
                  partnerId={proposalTarget?.id}
                  onChange={setTermsDiff}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-[#1A1A1A] font-medium">Sent. Their link:</p>
                <div className="bg-[#FAFAF8] border border-[#E2E2DF] rounded-md p-3 break-all text-xs font-mono">
                  {lastProposal.url}
                </div>
                <p className="text-sm text-[#64748B]">
                  Rate card v{lastProposal.rate_card_version}
                  {lastProposal.eligible_for_reseller
                    ? ' · reseller is open to them'
                    : ' · reseller shows as needing a word with us'}
                </p>
                <Link to={`/proposal/${lastProposal.token}`} target="_blank"
                      className="text-sm text-[#ee785b] font-semibold hover:underline inline-block">
                  Open it as they&apos;ll see it →
                </Link>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => { setProposalTarget(null); setLastProposal(null); }}>
                {lastProposal ? 'Done' : 'Cancel'}
              </Button>
              {!lastProposal && (
                <Button onClick={generateProposal} disabled={generating}
                        data-testid="generate-proposal">
                  {generating ? 'Generating…' : 'Generate and send'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* What this partner agreed to. Three artefacts, kept apart: the pinned rate card (the
            substance), the proposals (the conversation), and the terms acceptance (the
            clickwrap, with its evidence — that fingerprint is the whole reason the record is
            worth keeping, and until now nobody could see it). */}
        <Dialog open={!!agreementTarget} onOpenChange={() => { setAgreementTarget(null); setAgreement(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Agreement — {agreementTarget?.company_name || agreementTarget?.full_name}
              </DialogTitle>
            </DialogHeader>
            {!agreement ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-[#64748B]" />
              </div>
            ) : (
              <div className="space-y-5" data-testid="agreement-dialog">

                {/* The executed agreement, first, because it is the only artefact here that
                    binds and the only one the money gates read. Its absence is the interesting
                    state — that is a partner who cannot be paid. */}
                <div>
                  <p className="text-xs uppercase tracking-wide text-[#64748B] mb-2">
                    Partnership agreement
                  </p>
                  {!agreement.agreement ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      <p className="font-medium">Not issued.</p>
                      <p className="mt-1 text-xs">
                        {(agreement.missing_particulars || []).length
                          ? <>Waiting on the partner for: {agreement.missing_particulars.join(', ')}.</>
                          : <>Their details are complete — approving them issues it, or use “Issue agreement”.</>}
                        {' '}Payouts and credit purchases stay closed until it is signed.
                      </p>
                    </div>
                  ) : agreement.agreement.status === 'signed' ? (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
                      <p className="text-green-900">
                        <span className="font-semibold">Accepted</span> by{' '}
                        {agreement.agreement.signed_by_name} on{' '}
                        {agreement.agreement.signed_at
                          ? new Date(agreement.agreement.signed_at).toLocaleString('en-IN') : '—'}
                        {' · '}{agreement.agreement.signature_method}
                      </p>
                      <p className="mt-1 break-all text-xs text-green-800">
                        {agreement.agreement.particulars?.legal_name}
                        {' · '}fingerprint {(agreement.agreement.document_hash || '').slice(0, 24)}…
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-[#E2E2DF] p-3 text-sm">
                      <p className="text-[#1A1A1A]">
                        <span className="font-semibold capitalize">{agreement.agreement.status}</span>
                        {' · '}issued{' '}
                        {agreement.agreement.issued_at
                          ? new Date(agreement.agreement.issued_at).toLocaleDateString('en-IN') : '—'}
                        {' — waiting on them to accept it'}
                      </p>
                      <p className="mt-1 text-xs text-[#64748B]">
                        {agreement.agreement.particulars?.legal_name} · signing:{' '}
                        {agreement.agreement.particulars?.signatory_name}
                      </p>
                    </div>
                  )}
                  {agreement.agreement && (
                    <>
                      <Button variant="outline" size="sm" className="mt-3"
                              onClick={() => readAgreement(agreementTarget.id)}
                              data-testid="read-agreement-admin">
                        {agreementDoc ? 'Hide the agreement' : 'Read the agreement'}
                      </Button>
                      {agreementDoc && (
                        <div className="mt-3 max-h-96 overflow-y-auto rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-4"
                             data-testid="agreement-document">
                          {renderMarkdown(agreementDoc)}
                        </div>
                      )}
                    </>
                  )}
                  {agreement.stage && (
                    <p className="mt-2 text-xs text-[#64748B]">
                      Journey: {String(agreement.stage).replace(/_/g, ' ')}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-[#64748B] mb-2">
                    Pinned rate card
                  </p>
                  <p className="text-sm text-[#1A1A1A]">
                    <span className="font-semibold">v{agreement.rate_card?.version}</span>
                    {' · '}{agreement.tier || 'silver'} tier
                  </p>
                  {/* What they earn at their CURRENT tier, on each way a client can be
                      engaged. A partner has no single rate any more — they have a tier, and
                      the rate follows whichever arrangement each client is on. */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {['solution', 'referral', 'affiliate'].map((arrangement) => {
                      const rate = ((agreement.rate_card?.commission || {})[arrangement] || {})[
                        agreement.tier || 'silver'];
                      const label = rate == null
                        ? '—'
                        : typeof rate === 'object'
                          ? `₹${rate.flat}/client`
                          : `${Math.round(Number(rate) * 1000) / 10}%`;
                      return (
                        <span key={arrangement} className="text-xs bg-[#F4F4F2] border border-[#E2E2DF] rounded-full px-2 py-1 capitalize">
                          {arrangement} {label}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-[#64748B] mb-2">
                    Partner terms
                  </p>
                  {(agreement.terms?.accepted || []).length === 0 ? (
                    <p className="text-sm text-[#64748B]">No acceptance on record.</p>
                  ) : (
                    <div className="space-y-2">
                      {agreement.terms.accepted.map((a, i) => (
                        <div key={i} className="text-sm border border-[#E2E2DF] rounded-lg p-3">
                          <p className="text-[#1A1A1A]">
                            <span className="font-semibold">v{a.terms_version}</span> accepted{' '}
                            {a.accepted_at ? new Date(a.accepted_at).toLocaleString('en-IN') : '—'}
                            {a.context ? ` · at ${a.context}` : ''}
                          </p>
                          <p className="text-xs text-[#64748B] mt-1 break-all">
                            from {a.ip || 'unknown ip'} · content hash {(a.terms_hash || '').slice(0, 16)}…
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {agreement.terms?.needs_acceptance && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 mt-2">
                      Current terms are v{agreement.terms.current_version} — this partner has not
                      accepted the latest version.
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-[#64748B] mb-2">Proposals</p>
                  {agreement.proposals.length === 0 ? (
                    <p className="text-sm text-[#64748B]">None sent.</p>
                  ) : (
                    <div className="space-y-2">
                      {agreement.proposals.map((p) => (
                        <div key={p.token} className="flex items-center justify-between gap-3 text-sm border border-[#E2E2DF] rounded-lg p-3">
                          <div>
                            <p className="text-[#1A1A1A] capitalize">
                              {p.status} · card v{p.rate_card_version}
                            </p>
                            <p className="text-xs text-[#64748B] mt-0.5">
                              Sent {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '—'}
                              {p.decided_at ? ` · decided ${new Date(p.decided_at).toLocaleDateString('en-IN')}` : ''}
                            </p>
                          </div>
                          <a href={`/proposal/${p.token}`} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline">Open</Button>
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Three artefacts, three weights (L57). Saying which is which here is the
                    whole point of showing them apart — the terms row is a tick-box on generic
                    house rules, the proposal binds nobody, and only the agreement is a contract. */}
                <p className="text-xs text-[#64748B] border-t border-[#E2E2DF] pt-3">
                  Only the <strong>partnership agreement</strong> binds. Partner terms are a
                  clickwrap on the house rules, and a proposal sets the rates without committing
                  either side.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Set password — the out-of-band fallback when a partner cannot get the reset email */}
        <Dialog open={!!passwordTarget} onOpenChange={() => { setPasswordTarget(null); setNewPassword(''); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Set password</DialogTitle>
            </DialogHeader>
            {passwordTarget && (
              <form onSubmit={setPartnerPassword} className="space-y-4">
                <p className="text-sm text-[#64748B]">
                  Sets a new password for{' '}
                  <span className="font-medium text-[#1A1A1A]">{passwordTarget.full_name}</span>{' '}
                  ({passwordTarget.email}). Use this only when they cannot receive the reset
                  email — you will need to pass the password to them yourself. They are told it
                  happened, but not what it is.
                </p>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                  autoFocus
                  className="h-10 border-[#E2E2DF]"
                  data-testid="set-password-input"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setPasswordTarget(null); setNewPassword(''); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={savingPassword}
                    className="bg-[#ee785b] hover:bg-[#d66547] text-white"
                    data-testid="set-password-submit"
                  >
                    {savingPassword ? 'Saving...' : 'Set password'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Partner Detail Modal */}
        <Dialog open={!!selectedPartner} onOpenChange={() => setSelectedPartner(null)}>
          {/* max-h + overflow, or the bottom of this dialog is unreachable. It grew past the
              viewport the moment the answers and the paperwork went in, and a Radix dialog with
              no height cap does not scroll — it just runs off the screen, taking both Save
              buttons with it. Same pattern as the other long dialogs in this repo. */}
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Partner Details</DialogTitle>
            </DialogHeader>
            {selectedPartner && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-[#FAFAF8] rounded-lg">
                  <div className="w-16 h-16 rounded-full bg-[#ef785b]/10 flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#ef785b]">
                      {selectedPartner.full_name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-lg text-[#1A1A1A]">{selectedPartner.full_name}</p>
                    <p className="text-sm text-[#64748B]">{selectedPartner.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <TierBadge tier={selectedPartner.tier} />
                      <StatusBadge status={selectedPartner.status} />
                    </div>
                  </div>
                </div>

                {/* The paperwork, where you are already standing. This is the question the
                    partner record gets opened to answer — what did they agree to, and have they
                    signed — and sending someone to a different screen for it is how "who owes us
                    a signature" stays unknown. */}
                <div className="rounded-xl border border-[#E2E2DF] bg-[#FAFAF8] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs uppercase tracking-wider text-[#64748B]">Paperwork</p>
                    <JourneyCell partner={selectedPartner} />
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <p className="text-xs text-[#64748B]">Proposal</p>
                      {selectedPartner.proposal ? (
                        <>
                          <p className="text-sm font-medium capitalize text-[#1A1A1A]">
                            {selectedPartner.proposal.status}
                            {' · '}card v{selectedPartner.proposal.rate_card_version}
                          </p>
                          <p className="text-xs text-[#64748B]">
                            Sent {formatDate(selectedPartner.proposal.created_at)}
                            {selectedPartner.proposal.decided_at
                              ? ` · decided ${formatDate(selectedPartner.proposal.decided_at)}` : ''}
                            {selectedPartner.proposal_count > 1
                              ? ` · ${selectedPartner.proposal_count} sent in total` : ''}
                          </p>
                          <a href={`/proposal/${selectedPartner.proposal.token}`} target="_blank"
                             rel="noreferrer"
                             className="text-xs font-semibold text-[#ee785b] hover:underline">
                            Open it as they see it →
                          </a>
                        </>
                      ) : (
                        <p className="text-sm text-[#64748B]">None sent</p>
                      )}
                    </div>

                    <div>
                      <p className="text-xs text-[#64748B]">Agreement</p>
                      {selectedPartner.agreement ? (
                        <>
                          <p className="text-sm font-medium capitalize text-[#1A1A1A]">
                            {selectedPartner.agreement.status}
                          </p>
                          <p className="text-xs text-[#64748B]">
                            {selectedPartner.agreement.signed_at
                              ? `Signed ${formatDate(selectedPartner.agreement.signed_at)} by ${selectedPartner.agreement.signed_by_name}`
                              : `Issued ${formatDate(selectedPartner.agreement.issued_at)} · waiting on their signature`}
                          </p>
                          {selectedPartner.agreement.legal_name && (
                            <p className="text-xs text-[#64748B]">{selectedPartner.agreement.legal_name}</p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-[#64748B]">Not issued</p>
                      )}
                      <button type="button"
                              onClick={() => { setSelectedPartner(null); openAgreement(selectedPartner); }}
                              className="mt-1 text-xs font-semibold text-[#ee785b] hover:underline">
                        Full record →
                      </button>
                    </div>
                  </div>
                </div>

                <BrandingGrant partner={selectedPartner} />

                <RatesAndGatesPanel
                  partner={selectedPartner}
                  onSendProposal={() => {
                    const p = selectedPartner;
                    setSelectedPartner(null);
                    setProposalTarget(p);
                    setTermsDiff({});
                  }}
                />

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Tier</p>
                    <p className="font-medium text-[#1A1A1A] capitalize">{selectedPartner.tier}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Mobile</p>
                    <p className="font-medium text-[#1A1A1A]">
                      {selectedPartner.mobile ? (
                        <a href={`tel:${selectedPartner.mobile}`} className="text-[#ef785b] underline">{selectedPartner.mobile}</a>
                      ) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">City / State</p>
                    <p className="font-medium text-[#1A1A1A]">
                      {selectedPartner.city || '—'}{selectedPartner.state ? `, ${selectedPartner.state}` : ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Company</p>
                    <p className="font-medium text-[#1A1A1A]">{selectedPartner.company_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Designation</p>
                    <p className="font-medium text-[#1A1A1A]">{selectedPartner.designation || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Joined</p>
                    <p className="font-medium text-[#1A1A1A]">{formatDate(selectedPartner.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">Referral Source</p>
                    <p className="font-medium text-[#1A1A1A] capitalize">{selectedPartner.referral_source || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">LinkedIn</p>
                    <p className="font-medium text-[#1A1A1A]">
                      {selectedPartner.linkedin_url ? (
                        <a href={selectedPartner.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#ef785b] underline">View Profile</a>
                      ) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">WhatsApp</p>
                    <p className="font-medium text-[#1A1A1A]">{selectedPartner.whatsapp_enabled ? 'Enabled' : 'Disabled'}</p>
                  </div>
                </div>
                {/* What they told us about their book — editable, in the application's own
                    vocabulary.

                    This block used to be gated on `selectedPartner.icp_band` and read-only.
                    Nothing computes a band any more (see the header of backend/scorecard.py),
                    so the gate was never true and the answers had quietly stopped appearing
                    on this screen at all. They are the whole reason an admin opens it.

                    Editable because this is also where what a rep learns on a call lands. The
                    endpoint that used to take those notes wrote client_count and team_size as
                    INTEGERS over the same keys the form stores as buckets, and emitted nothing
                    — it is retired. Every change here reaches the CRM as a delta. */}
                <div className="rounded-lg border border-[#E2E2DF] p-4">
                  <p className="text-xs uppercase tracking-wider text-[#64748B]">The application</p>
                  <p className="mb-4 mt-1 text-xs text-[#64748B]">
                    Their own answers. Correct anything a call has since changed.
                  </p>
                  <ApplicationAnswers
                    endpoint={`${API_URL}/api/admin/partners/${selectedPartner.id}/application`}
                    headers={getAuthHeaders()}
                    answers={applicationAnswersOf(selectedPartner)}
                    onSaved={(answers) => {
                      setSelectedPartner((prev) => ({ ...prev, ...answers }));
                      fetchPartners();
                    }}
                  />
                </div>
                {selectedPartner.network_description && (
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Anything else they told us</p>
                    <p className="text-sm text-[#1A1A1A] bg-[#FAFAF8] p-3 rounded-lg">{selectedPartner.network_description}</p>
                  </div>
                )}
                {/* What it takes to bill them and to pay them, in the two groups those two jobs
                    are actually done in. The data was always on this response — nothing here
                    renders it, which is why creating a Zoho customer meant opening the database.

                    Editable, because some of it arrives on a call and never gets typed into the
                    portal — and until it does the partner cannot be billed or paid. Both panels
                    save through the SAME backend path the partner's own Profile uses, so an
                    admin is not a way around the validation, the fraud audit, or the notice the
                    partner gets when their payout destination moves. */}
                <PartnerPaperwork
                  partner={selectedPartner}
                  apiUrl={API_URL}
                  headers={getAuthHeaders()}
                  onSaved={fetchPartners}
                />

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#E2E2DF]">
                  <div className="p-4 bg-[#FAFAF8] rounded-lg text-center">
                    <p className="text-xs text-[#64748B]">Active Clients</p>
                    <p className="text-2xl font-bold text-[#1A1A1A]">{selectedPartner.active_clients || 0}</p>
                  </div>
                  <div className="p-4 bg-[#FAFAF8] rounded-lg text-center">
                    <p className="text-xs text-[#64748B]">Client MRR</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedPartner.total_revenue || 0)}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminPartners;
